import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import { Formik, Form } from 'formik';
import { noop } from 'lodash';
import * as Yup from 'yup';

import BaseLayout from './BaseLayout';
import Button from './Button';
import ClickAwayListener from './ClickAwayListener';
import Menu, { MenuItem } from './Menu';
import InitialAvatar from './InitialAvatar';
import { useSessionContext } from '../contexts/sessionContext';
import { useTenantContext } from '../contexts/tenantContext';
import SearchInput from './form/SearchInput';
import { getTripsFetchBySpecification, getOrganizationsFetchBySpecification } from './trip/helper';
import SearchIcon from '../components/icons/searchIcon';
import ArrowDownIcon from '../components/icons/arrowDownIcon';
import CloseIcon from '../components/icons/closeIcon';
import { GraphIcon } from '../components/icons/graphIcon'
import { SideNavModal } from '../components/modals/SideNavModal';
import { useTranslation, initReactI18next } from "react-i18next";
import { MailIcon } from './icons/mailIcon';
import { getTenantDetails } from '../api/organizations';
import SidebarLinkH from './SidebarLinkH';
import DeleteIcon from '../components/Delete';
import ConveyanceIcon from './icons/conveyanceIcon';
import BoxIcon from './icons/boxIcon';
import TrashIcon from './icons/trashIcon';
import { SearchSelect } from './select-v2/SearchSelect';
const blobUrl = process.env.NEXT_PUBLIC_BLOB_URL;
import { fetchTripsByTripNumber, fetchTripsByScn } from '../api/tripService';
import { getPortalNotification } from '../api/users';
import {
  countUnreadActiveNotifications,
  getOpenedNotificationIds,
  getUserCustomsFlags,
} from '../utils/notificationAudience';


export default function MainLayout({ children, fullScreen = true }) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, logout, currentOrganization, refreshUserAsync, userData, token } = useSessionContext();
  const { currentTenant } = useTenantContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const [newTripMenuOpen, setNewTripMenuOpen] = useState(false);
  const [isAdminTenant, setIsAdminTenant] = useState(false);
  const [initialValues] = useState({
    trip: null,
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [activeTripTab, setActiveTripTab] = useState(true);
  const [activeShipmentTab, setActiveShipmentTab] = useState(false);
  const [tripResults, setTripResults] = useState([]);
  const [tripLoading, setTripLoading] = useState(false);
  const [shipmentResults, setShipmentResults] = useState([]);
  const [shipmentLoading, setShipmentLoading] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const organization = currentOrganization || null;
  let shouldShowTripButton = !['/', '/truck/trip'].includes(router.pathname);
  const readOnlyUser = user.readOnlyMode;
  const shouldShowOrganizationSelect = router.pathname.indexOf("dashboard") > 0;
  const notificationButton = localStorage.getItem('notificationButton') !== 'false';
  const helperButton = localStorage.getItem('helperButton') !== 'false';
  const organizationFinder = localStorage.getItem('organizationFinder') !== 'false';
  const profile = localStorage.getItem('profile') !== 'false';



  useEffect(() => {
    if (currentOrganization && token) {
      getTenantDetails(currentOrganization.id, token)
        .then(async (tenantDetails) => {
          if (user?.adminTenants.length && user?.adminTenants && user?.adminTenants.includes(tenantDetails.id)) {
            setIsAdminTenant(true)
          }
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [currentOrganization]);

  useEffect(() => {
    const loadNotificationCount = async () => {
      if (!token || !currentOrganization?.tenantId) {
        setNotificationsCount(0);
        return;
      }
      try {
        const response = await getPortalNotification(token, true);
        const notifications = response.notification || [];
        const flags = getUserCustomsFlags();
        const openedIds = getOpenedNotificationIds(user?.id, currentOrganization.tenantId);
        setNotificationsCount(
          countUnreadActiveNotifications(
            notifications,
            currentOrganization.tenantId,
            flags,
            openedIds
          )
        );
      } catch (error) {
        console.error('loadNotificationCount error', error);
        setNotificationsCount(0);
      }
    };

    loadNotificationCount();

    const refreshNotificationCount = () => {
      loadNotificationCount();
    };
    window.addEventListener('portal-notifications-updated', refreshNotificationCount);
    return () => {
      window.removeEventListener('portal-notifications-updated', refreshNotificationCount);
    };
  }, [token, currentOrganization?.tenantId, user?.id]);

  if (localStorage.getItem("showNewTrip") == "false") {
    shouldShowTripButton = false;
  }

  const handleSelectOrg = async (value) => {
    await refreshUserAsync(value, userData.root ? "Support" : null);
  };



  const handleSearchChange = (tripId, resetForm) => {
    if (tripId) {
      router.push({
        pathname: '/truck/trip/summary',
        query: { tripId },
      });
      setTimeout(resetForm, 500);
    }
  };

  const handleChangeOrganization = async (organization) => {
    if (organization) {
      await handleSelectOrg(organization)
      setTimeout(500);
    }
  };

  const handleTripSearchTextChange = async (text) => {

    if (!text || text.length <= 2 || !token || !currentOrganization?.id) {
      setTripResults([]);
      setTripLoading(false);
      return;
    }

    try {
      setTripLoading(true);
      let data = await fetchTripsByTripNumber(token, currentOrganization.id, text);

      data = data.map(item => {
        return {
          id: item.id,
          tripNumber: `${item.SCAC}-${item.tripNumber}`,
        }
      })

      const arr = Array.isArray(data) ? data : [];
      setTripResults(arr);
    } catch (error) {
      console.error('handleTripSearchTextChange error', error);
      setTripResults([]);
    } finally {
      setTripLoading(false);
    }
  };

  const handleShipmentSearchTextChange = async (text) => {
    if (!text || text.length <= 2 || !token || !currentOrganization?.id) {
      setShipmentResults([]);
      setShipmentLoading(false);
      return;
    }

    try {
      setShipmentLoading(true);
      let data = await fetchTripsByScn(token, currentOrganization.id, text);
      data = data.map(item => {
        return {
          id: item.id,
          scn: `${item.SCAC}-${item.scn}`,
        }
      })

      const arr = Array.isArray(data) ? data : [];
      setShipmentResults(arr);

    } catch (error) {
      console.error('Shipment search error', error);
      setShipmentResults([]);
    } finally {
      setShipmentLoading(false);
    }
  };

  const renderHeaderOptions = (legend = "") => {
    let buttonLegend = "";

    buttonLegend = `${t('Close')} ${legend}`;
    return (
      <Button size="small" className="ml-auto" dropdown onClick={() => setModalOpen(false)}>
        {buttonLegend}
      </Button>
    );
  };

  const fetchByTrips = useMemo(() => {
    const { fetchBy } = getTripsFetchBySpecification();
    return fetchBy.trips;
  }, []);

  const fetchByOrganizations = useMemo(() => {
    const { fetchBy } = getOrganizationsFetchBySpecification();
    return fetchBy.organizations;
  }, []);

  const headerComponent = (
    <>
      {(user.root || shouldShowTripButton && !readOnlyUser) && (
        <>
          <ClickAwayListener onClickAway={() => setNewTripMenuOpen(false)}>
            <div className="relative flex ml-auto">
              <Button
                primary
                className="rounded-r-none"
                onClick={() => router.push('/truck/trip')}
              >
                {t('New Trip')}
              </Button>
              <div className="w-px bg-white" />
              <Button
                primary
                className="rounded-l-none"
                style={{ paddingLeft: 6, paddingRight: 6 }}
                onClick={() => setNewTripMenuOpen((isOpen) => !isOpen)}
              >
                <ArrowDownIcon width={10} height={8} />
              </Button>
              {newTripMenuOpen && (
                <Menu className="absolute right-0 top-full mt-1 w-32 z-10">
                  <MenuItem
                    onClick={() => {
                      setNewTripMenuOpen(false);
                      router.push('/truck/trip/ftp');
                    }}
                  >
                    {t('FTP')}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setNewTripMenuOpen(false);
                      router.push('/truck/trip/api');
                    }}
                  >
                    {t('API')}
                  </MenuItem>
                </Menu>
              )}
            </div>
          </ClickAwayListener>
          <div className="h-8 bg-gray-400 w-px m-1 md:ml-3" />
        </>
      )}

      <Formik
        validationSchema={Yup.object({ Trip: Yup.string().nullable() })}
        initialValues={initialValues}
        onSubmit={noop}
        enableReinitialize
      >
        {(props) => {
          return (
            <Form autoComplete="off">

              <div className="flex flex-direction-column mr-2">
                {shouldShowOrganizationSelect && organizationFinder && (
                  <>
                    <div className="hidden md:block md:w-34 lg:w-42 xl:w-56 mx-3">
                      <SearchInput
                        onChange={(e) => handleChangeOrganization(e)}
                        name="organization"
                        hintText={t("Search Organization")}
                        fetchBy={fetchByOrganizations}
                        placeHolder={t("Search Organization")}
                        emptySearchMessage={t("No organizations found")}
                        icon={<SearchIcon />}
                      />
                    </div>
                    <div className="h-8 bg-gray-400 w-px m-1 md:ml-3" />
                  </>
                )}
                <div className={activeTripTab ? 'custom-truck-icon path' : ''}>
                  <ConveyanceIcon
                    className="mt-2 mx-1 cursor-pointer"
                    width={32}
                    height={28}
                    onClick={() => setActiveTripTab((prev) => !prev)}
                  />
                </div>
                {activeTripTab ? (
                  <div className="hidden md:block md:w-34 lg:w-48 xl:w-48 mx-3">
                    <SearchSelect
                      placeholder={t('Search Trips')}
                      items={tripResults}
                      loading={tripLoading}
                      onChangeText={handleTripSearchTextChange}
                      getLabel={(trip) => trip.tripNumber}
                      t={t}
                      onSelect={(trip) => {
                        handleSearchChange(trip.id, props.resetForm);
                      }}
                    />
                  </div>
                ) : (
                  <div className='flex cursor-pointer mr-2' onClick={() => {
                    setActiveTripTab(true)
                  }}>
                    <span className='text-gray-500 text-xs mt-3 hover:text-gray-700'>{t('Search Trips')}</span>
                  </div>

                )}
                <div className={activeShipmentTab ? 'custom-box-icon path' : ''}>
                  <BoxIcon

                    className="mt-2 mr-1 cursor-pointer"
                    onClick={() =>
                      setActiveShipmentTab((prev) => !prev)
                    }
                    fill={activeShipmentTab}
                  />
                </div>
                {activeShipmentTab ? (
                  <div className="hidden md:block md:w-34 lg:w-40 xl:w-48 mx-3">
                    <SearchSelect
                      placeholder={t('Search Shipment')}
                      items={shipmentResults}
                      loading={shipmentLoading}
                      onChangeText={handleShipmentSearchTextChange}
                      getLabel={(shipment) => shipment.scn}
                      t={t}
                      onSelect={(shipment) => {
                        if (shipment.id) {
                          handleSearchChange(shipment.id, props.resetForm);
                        }
                      }}
                    />
                  </div>
                ) : (

                  <div className='flex cursor-pointer' onClick={() => setActiveShipmentTab(true)}>

                    <span className='text-gray-500 text-xs mt-3 mr-1 hover:text-gray-700'>{t('Search Shipment')}</span>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>



      <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
        <div
          onClick={() => setMenuOpen((isOpen) => !isOpen)}
          className="flex items-center space-x-2 cursor-pointer"
        >
          {user && profile && <InitialAvatar firstName={user.firstName} lastName={user.lastName} />}
          {profile && (
            <div className="hidden md:block">
              <div className="font-semibold">
                {user && (
                  <span className="text-xs lg:text-sm">
                    {user.firstName} {user.lastName}
                  </span>
                )}
              </div>
              <div className="text-xs lg:text-sm text-gray-600">
                {organization && organization.name}
              </div>
            </div>
          )}
          {menuOpen && (
            <Menu className="user-menu absolute">
              <MenuItem onClick={() => router.push('/settings/profile')}>
                {t('Member Settings')}
              </MenuItem><MenuItem onClick={() => router.push('/settings/organization')}>
                {t('Organization Settings')}
              </MenuItem>
              <MenuItem onClick={() => router.push('/reports/reports')}>
                {t('Reports')}
              </MenuItem>
              <MenuItem
                className="text-red-400 border-t border-gray-300"
                onClick={logout}
              >
                {t('Sign out')}
              </MenuItem>
              {
                isAdminTenant &&
                <MenuItem onClick={() => router.push('/settings/tenant')}>
                  {t('Tenant Settings')}
                </MenuItem>
              }
            </Menu>
          )}
        </div>
      </ClickAwayListener>
    </>
  );

  return (
    <BaseLayout
      fullScreen={fullScreen}
      logo={<img src={`${blobUrl}${currentTenant.id}.png`} className="cursor-pointer h-3 md:h-4 lg:h-6" />}
      menu={[
        {
          icon: null,
          label: t('Dashboard'),
          href: '/truck',
          hide: false
        },
        {
          icon: null,
          label: t('Catalogs'),
          href: '/catalogs',
          hide: user?.canEditCatalogs ? false : true
        },
        {
          icon: null,
          label: t('Support'),
          href: '/support',
          hide: true
        },
        {
          icon: <GraphIcon width={20}></GraphIcon>,
          label: null,
          href: '/support/dashboard',
          hide: true
        },
        ...(notificationButton
          ? [
            {
              icon: (
                <span className="relative inline-flex">
                  <MailIcon width={20} />
                  {notificationsCount > 0 && (
                    <span
                      className="absolute -right-1.5 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm"
                      aria-label={`${notificationsCount} notifications`}
                    >
                      {notificationsCount > 99 ? '99+' : notificationsCount}
                    </span>
                  )}
                </span>
              ),
              label: null,
              href: '/support/notificationHistory',
              hide: false,
            },
          ]
          : []),
      ]}
      header={headerComponent}
      className={`h-full ${fullScreen && "pt-16"}`}
      isRoot={userData?.userInfo?.root}
    >
      {isModalOpen && (
        <>
          <SideNavModal
            onClose={() => confirmRef.current.open()}
            contentDirection="right"
          >
            <div className="bg-white h-full w-full">

              <div className="border-gray-200 border-t border-b py-4 px-8 bg-white">
                <div className="flex items-center">
                  <span
                    className="cursor-pointer"
                    onClick={() => setModalOpen(false)}
                  >
                    <CloseIcon />
                  </span>
                  <h1 className="text-gray-700 font-semibold text-base ml-3">
                    {t("Contact Information")}
                  </h1>
                  {renderHeaderOptions(t("Contact Information"))}
                </div>
              </div>

              <div className="md:m-20 p-4 sm:p-8 flex-grow overflow-y-auto md:bg-white rounded-md">
                <div className="flex flex-col items-center pt-15 pb-15 sm:pt-3 mt-10 bg-white rounded-md">
                  <img className="w-1/2 mb-12 sm:mb-8" src="/images/call-center.png" />
                  <div className="flex flex-col items-center w-full md:p-5">
                    <h1 className="font-semibold text-gray-600 mt-3 text-md">{t('Phone Number')}</h1>
                    <span className="mt-1 text-gray-700 text-xl">{currentTenant.phoneNumberShow}</span>
                  </div>
                  <div className="flex flex-col items-center mt-3 w-full p-5">
                    <h1 className="font-semibold text-gray-600 text-md">{t('Email')}</h1>
                    <span className="mt-1 text-gray-700 text-xl">{currentTenant.emailShow}</span>
                  </div>
                </div>
              </div>
            </div>
          </SideNavModal>
        </>
      )}
      <div className="m-auto flex-col h-full">{children}</div>
      {helperButton && (
        <Button primary className="fixed bottom-0 right-0 mr-2 mb-2" onClick={() => setModalOpen(true)}>
          {t('Need Help?')}
        </Button>
      )}
    </BaseLayout>
  );
}
